<script lang="ts">
	import { page } from '$app/stores';
	import { utilitiesTools } from '$lib/links.js';
	import { Home, ChevronRight, ChevronUp } from '@lucide/svelte';
	import { onMount, afterUpdate } from 'svelte';

	const UTILITIES_BASE_PATH = '/utilities';
	let breadcrumbs: { name: string; href?: string }[] = [];
	
	// Go to top button state
	let showGoToTop = false;

	function formatSegmentName(segment: string): string {
		return segment
			.split('-')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	// Get page title for sharing
	$: pageTitle = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1]?.name || 'Utility Tool' : 'Utility Tool';

	$: {
		const currentRelativePath = $page.url.pathname.startsWith(UTILITIES_BASE_PATH)
			? $page.url.pathname.substring(UTILITIES_BASE_PATH.length)
			: '';
		const currentSegments = currentRelativePath.split('/').filter(s => s);

		breadcrumbs = [
			{ name: 'Home', href: '/' },
			{ name: 'Utilities', href: UTILITIES_BASE_PATH }
		];

		let currentHref = UTILITIES_BASE_PATH;
		currentSegments.forEach((segment, index) => {
			currentHref += `/${segment}`;
			const name = formatSegmentName(segment);

			if (name) {
				breadcrumbs.push({
					name,
					href: index < currentSegments.length - 1 ? currentHref : undefined
				});
			}
		});
	}

	// Load AddToAny script with better timing
	function loadAddToAnyScript() {
		if (typeof window !== 'undefined' && !(window as any).a2a) {
			const script = document.createElement('script');
			script.async = true;
			script.src = 'https://static.addtoany.com/menu/page.js';
			script.onload = () => {
				// Reinitialize AddToAny after script loads
				if ((window as any).a2a) {
					(window as any).a2a.init_all();
				}
			};
			document.head.appendChild(script);
		} else if ((window as any).a2a) {
			// Reinitialize if script already exists
			(window as any).a2a.init_all();
		}
	}

	// Handle scroll for go-to-top button
	function handleScroll() {
		if (typeof window !== 'undefined') {
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
			const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
			const scrollPercentage = (scrollTop / scrollHeight) * 100;
			
			// Show button when scrolled down more than 50%
			showGoToTop = scrollPercentage > 50;
		}
	}

	// Scroll to top function
	function scrollToTop() {
		if (typeof window !== 'undefined') {
			window.scrollTo({
				top: 0,
				behavior: 'smooth'
			});
		}
	}

	onMount(() => {
		loadAddToAnyScript();
		
		// Add scroll event listener
		if (typeof window !== 'undefined') {
			window.addEventListener('scroll', handleScroll);
			
			// Cleanup function
			return () => {
				window.removeEventListener('scroll', handleScroll);
			};
		}
	});

	afterUpdate(() => {
		// Reinitialize AddToAny after page navigation
		if (typeof window !== 'undefined' && (window as any).a2a) {
			setTimeout(() => {
				(window as any).a2a.init_all();
			}, 100);
		}
	});
</script>

<div class="flex flex-grow w-full">
	<aside class="hidden md:block md:w-1/6 bg-gray-50 border-r border-gray-200 p-4 md:sticky md:top-0 md:h-screen md:overflow-y-auto">
		<h2 class="text-lg font-semibold text-gray-800 mb-4">Utility Tools</h2>
		<ul class="space-y-2">
			{#each utilitiesTools as tool (tool.href)}
				<li>
					<a
						href={tool.href}
						class="block p-3 rounded-lg transition-colors duration-150"
						class:bg-blue-100={$page.url.pathname === tool.href}
						class:text-blue-700={$page.url.pathname === tool.href}
						class:font-medium={$page.url.pathname === tool.href}
						class:hover:bg-gray-100={$page.url.pathname !== tool.href}
						class:text-gray-600={$page.url.pathname !== tool.href}
						class:hover:text-gray-900={$page.url.pathname !== tool.href}
					>
						<div class="font-medium text-sm">{tool.text}</div>
						<div class="text-xs mt-1 opacity-80">{tool.description}</div>
					</a>
				</li>
			{/each}
		</ul>
	</aside>

	<main class="w-full md:w-5/6 p-4 md:h-screen md:overflow-y-auto">
		<!-- Breadcrumbs with Share Buttons -->
		<div class="flex justify-between items-center mb-4">
			<nav aria-label="Breadcrumb">
				<ol class="flex items-center space-x-1 text-sm text-gray-500 flex-wrap">
					{#each breadcrumbs as breadcrumb, i (i)}
						<li class="flex items-center">
							{#if i > 0}
								<ChevronRight size={16} class="flex-shrink-0 text-gray-400 mx-1" />
							{/if}
							{#if breadcrumb.href}
								<a href={breadcrumb.href} class="hover:text-gray-700 hover:underline">
									{#if i === 0} <Home size={14} class="inline mr-1 align-text-bottom"/> {/if}
									{breadcrumb.name}
								</a>
							{:else}
								<span class="font-medium text-gray-600">{breadcrumb.name}</span>
							{/if}
						</li>
					{/each}
				</ol>
			</nav>
			
			<!-- AddToAny Share Buttons -->
			<div class="a2a_kit a2a_kit_size_32 a2a_default_style" data-a2a-url="" data-a2a-title="{pageTitle} - Utility Tool">
				<a class="a2a_dd" href="https://www.addtoany.com/share"></a>
				<a class="a2a_button_facebook"></a>
				<a class="a2a_button_twitter"></a>
				<a class="a2a_button_linkedin"></a>
				<a class="a2a_button_reddit"></a>
				<a class="a2a_button_telegram"></a>
				<a class="a2a_button_whatsapp"></a>
				<a class="a2a_button_copy_link"></a>
			</div>
		</div>

		<slot />

		<!-- Go to Top Button -->
		{#if showGoToTop}
			<button
				on:click={scrollToTop}
				class="fixed bottom-6 right-6 bg-pink-600 hover:bg-pink-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 ease-in-out opacity-80 hover:opacity-100 z-50"
				aria-label="Go to top"
				title="Go to top"
			>
				<ChevronUp size={24} />
			</button>
		{/if}

		<!-- Mobile Navigation -->
		<aside class="block md:hidden w-full bg-gray-50 border-t border-gray-200 p-4 mt-6">
			<h2 class="text-lg font-semibold text-gray-800 mb-4">Utility Tools</h2>
			<ul class="space-y-2">
				{#each utilitiesTools as tool (tool.href)}
					<li>
						<a
							href={tool.href}
							class="block p-3 rounded-lg transition-colors duration-150"
							class:bg-blue-100={$page.url.pathname === tool.href}
							class:text-blue-700={$page.url.pathname === tool.href}
							class:font-medium={$page.url.pathname === tool.href}
							class:hover:bg-gray-100={$page.url.pathname !== tool.href}
							class:text-gray-600={$page.url.pathname !== tool.href}
							class:hover:text-gray-900={$page.url.pathname !== tool.href}
						>
							<div class="font-medium text-sm">{tool.text}</div>
							<div class="text-xs mt-1 opacity-80">{tool.description}</div>
						</a>
					</li>
				{/each}
			</ul>
		</aside>
	</main>
</div> 